import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { useTheme } from '../ThemeContext';
import { Images } from '../assets';
import { AtiraColumn } from '../components/AtiraColumn';
import { AtiraRow } from '../components/AtiraRow';
import { Button } from '../components/Button';
import { Drawer } from '../components/Drawer';
import { Flex } from '../components/Flex';
import { AtiraImage } from '../components/Image';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { MultiLangLink } from '../components/MultiLangLink';
import i18n, { AppLangs } from '../i18n';
import { Breakpoints } from '../theme/Breakpoints';
import { FontSize } from '../theme/FontSize';
import { Rounded } from '../theme/Rounded';
import { Spacing } from '../theme/Spacing';
import { Header } from '../utils/local-antd';

const getHeaderLinks = (t: TFunction) => [
  {
    path: 'https://atiralink.com',
    title: t('common.gettingStarted'),
  },
  {
    path: 'https://index.atiralink.com',
    title: t('common.atiraIndex'),
    isExternal: true,
  },
  {
    path: 'https://atiralink.com/templates',
    title: t('common.templates'),
  },
  {
    path: 'https://atiralink.com/pricing',
    title: t('common.pricing'),
  },
  {
    path: 'https://atiralink.com/help',
    title: t('common.help_support'),
  },
];

const StyledHeader = styled(Header)`
  width: 100%;
  background-image: ${({ theme }) =>
    `linear-gradient(
    90deg, ${theme.main}
    ,${theme.main}
  )`};
  position: sticky;
  top: 0rem;
  z-index: 999;
  display: flex;
  align-items: center;
  height: fit-content;
  padding: ${Spacing.m};
  line-height: normal;
`;

const HeaderLink = styled(Link)<{ isActive?: boolean }>`
  color: ${({ theme, isActive }) => (isActive ? theme.activeMain : theme.sub)};
  font-weight: ${({ isActive }) => (isActive ? 'bold' : 'normal')};
  font-size: ${i18n.language === AppLangs.TR ? FontSize.xs : FontSize.s};
  text-decoration: none;
  border-radius: ${Rounded.sm};
  transition: all 0.3s;
  display: block;

  &:hover {
    color: ${({ theme }) => theme.lightMain} !important;
  }

  @media (min-width: ${Breakpoints.LARGE_DESKTOP}) {
    font-size: ${FontSize.m};
  }
`;

const HeaderLinksSection = styled(AtiraColumn)`
  display: flex;

  @media (max-width: ${Breakpoints.DESKTOP}) {
    display: none;
  }
`;

const HeaderLogoWrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  @media (min-width: ${Breakpoints.DESKTOP}) {
    justify-content: flex-start;
  }
`;

const MultiLangDrawerLink = styled(MultiLangLink)<{ isActive?: boolean }>`
  font-size: ${FontSize.l};
  color: ${({ theme, isActive }) =>
    isActive ? theme.lightMain : 'inherit'} !important;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.sub : 'transparent'};
  text-decoration: none;
  border-radius: ${Rounded.md};
  transition: all 0.3s;
  display: block;
  padding: 3px ${Spacing.s};

  &:hover {
    color: ${({ theme }) => theme.lightMain} !important;
    background-color: ${({ theme }) => theme.subHover};
  }
`;

const DrawerLink = styled(Link)<{ isActive?: boolean }>`
  font-size: ${FontSize.l};
  color: ${({ theme, isActive }) =>
    isActive ? theme.lightMain : 'inherit'} !important;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.sub : 'transparent'};
  text-decoration: none;
  border-radius: ${Rounded.md};
  transition: all 0.3s;
  display: block;
  padding: 3px ${Spacing.s};

  &:hover {
    color: ${({ theme }) => theme.lightMain} !important;
    background-color: ${({ theme }) => theme.subHover};
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    box-shadow: none !important;
  }
  .ant-drawer-header {
    border-bottom: 0;
  }
  .ant-drawer-title {
    font-size: ${FontSize.l};
  }
  .ant-drawer-header-title {
    flex-direction: row-reverse;
    justify-content: space-between;
    font-size: ${FontSize.m};
  }
  .anticon-close svg {
    background-color: ${({ theme }) => theme.sub};
    height: 1.8rem;
    width: 1.8rem;
    padding: 0.2rem;
    border-radius: ${Rounded.sm};
    transition: all 0.2s;
  }
  .anticon-close svg:hover {
    opacity: 0.7;
  }
  .anticon-close svg path {
    color: ${({ theme }) => theme.main};
  }
  * {
    color: ${({ theme }) => theme.sub};
  }
`;

const StyledNav = styled.nav`
  width: 100%;
`;

const HeaderLinksList = styled.ul`
  display: flex;
  gap: ${Spacing.l};
  align-items: center;
  justify-content: center;
  width: 100%;
  list-style-type: none;
  margin: 0;
`;

const DrawerLinksList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  list-style-type: none;
`;

const DrawerLinkLi = styled.li<{ isActive?: boolean }>`
  transition: all 0.3s;

  &:hover {
  }
`;

const ThemedDrawer = createGlobalStyle`
  .ant-drawer-content {
    background-color: ${({ theme }) => theme.main} !important;
  }
`;

const DesktopLanguageSwitcherWrapper = styled(Flex)`
  display: none !important;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: flex !important;
    margin-inline-end: ${Spacing.m};
  }
`;

export const PublicHeader: React.FC = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);

  const { t } = useTranslation();

  const location = useLocation();

  const { theme } = useTheme();

  const headerLinks = getHeaderLinks(t);

  const isActive = (path: string) => {
    const pathToCompare =
      i18n.language === AppLangs.EN ? path : `/${i18n.language}${path}`;

    return (
      (location.pathname.endsWith('/')
        ? location.pathname.slice(0, -1)
        : location.pathname) ===
      (pathToCompare.endsWith('/') ? pathToCompare.slice(0, -1) : pathToCompare)
    );
  };

  return (
    <StyledHeader>
      <AtiraRow
        align="middle"
        justify={'space-between'}
        gutter={true ? [0, 20] : [0, 0]}
      >
        <AtiraColumn xs={12} sm={12} md={12} lg={5} xl={5}>
          <HeaderLogoWrapper>
            <a
              href={`https://www.atiralink.com/${i18n.language !== AppLangs.EN ? i18n.language : ''}`}
            >
              <AtiraImage
                src={Images.AtiraLinkSubLogo}
                style={{ maxWidth: '20rem' }}
              />
            </a>
          </HeaderLogoWrapper>
        </AtiraColumn>

        <AtiraColumn xs={12} sm={12} md={12} lg={0} xl={0}>
          <Flex justifyContent="flex-end">
            <Button icon={faBars} onClick={() => setDrawerVisible(true)} />
            <LanguageSwitcher color="sub" />
          </Flex>
        </AtiraColumn>

        <HeaderLinksSection
          xs={0}
          sm={0}
          md={12}
          lg={i18n.language === AppLangs.AR ? 11 : 12}
          xl={12}
        >
          <StyledNav>
            <HeaderLinksList>
              {headerLinks.map((link) => (
                <li key={link.path}>
                  <HeaderLink to={link.path} target="_blank">
                    {link.title}
                  </HeaderLink>
                </li>
              ))}
            </HeaderLinksList>
          </StyledNav>
        </HeaderLinksSection>

        <AtiraColumn
          xs={0}
          sm={0}
          md={0}
          lg={i18n.language === AppLangs.AR ? 7 : 6}
          xl={6}
        >
          <AtiraRow align={'middle'} justify={'space-between'}>
            <AtiraColumn xs={6} sm={6} md={6} lg={0} xl={0}>
              <LanguageSwitcher color="sub" />
            </AtiraColumn>

            <AtiraColumn xs={18} sm={12} md={12} lg={24} xl={24}>
              <Flex justifyContent="flex-end" gap="s" alignItems="center">
                <DesktopLanguageSwitcherWrapper>
                  <LanguageSwitcher color="sub" />
                </DesktopLanguageSwitcherWrapper>
              </Flex>
            </AtiraColumn>
          </AtiraRow>
        </AtiraColumn>
      </AtiraRow>

      <StyledDrawer
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        placement="left"
      >
        <nav>
          <DrawerLinksList>
            {headerLinks.map((link) => (
              <DrawerLinkLi
                key={link.path}
                {...(!link.isExternal ? { isActive: isActive(link.path) } : {})}
              >
                {link.isExternal ? (
                  <DrawerLink
                    to={`${link.path}/${i18n.language !== AppLangs.EN ? i18n.language : ''}`}
                    {...(link.isExternal
                      ? { target: '_blank' }
                      : { isActive: isActive(link.path) })}
                    onClick={() => setDrawerVisible(false)}
                  >
                    {link.title}
                  </DrawerLink>
                ) : (
                  <MultiLangDrawerLink
                    to={link.path}
                    {...(link.isExternal
                      ? { target: '_blank' }
                      : { isActive: isActive(link.path) })}
                    onClick={() => setDrawerVisible(false)}
                  >
                    {link.title}
                  </MultiLangDrawerLink>
                )}
              </DrawerLinkLi>
            ))}
          </DrawerLinksList>
        </nav>
      </StyledDrawer>

      <ThemedDrawer theme={theme} />
    </StyledHeader>
  );
};
